import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { OrganizationType } from '@portal/api/enums/organization-type';

export type AppContext = 'portal' | 'club';

@Injectable({
    providedIn: 'root',
})
export class AppContextService {
    private readonly _localPortMap: Record<AppContext, string> = {
        portal: '4201',
        club: '4202',
    };

    constructor(@Inject(DOCUMENT) private readonly _document: Document) {
    }

    getContext(hostname = this.getLocation()?.hostname ?? ''): AppContext {
        const normalizedHostname = hostname.toLowerCase();
        const firstLabel = normalizedHostname.split('.')[0] ?? '';

        if (this.matchesContextLabel(firstLabel, 'club')) {
            return 'club';
        }

        if (this.matchesContextLabel(firstLabel, 'portal')) {
            return 'portal';
        }

        throw new Error(`Unable to resolve app context from hostname "${hostname}"`);
    }

    allowedOrgType(hostname = this.getLocation()?.hostname ?? ''): OrganizationType.Party | OrganizationType.Association {
        return this.getContext(hostname) === 'club'
            ? OrganizationType.Association
            : OrganizationType.Party;
    }

    targetHostForOrgType(type: OrganizationType | string | number | null | undefined): AppContext {
        const normalizedType = this.normalizeOrgType(type);

        if (normalizedType === null) {
            throw new Error(`Unable to resolve target host for organization type "${type}"`);
        }

        return normalizedType === OrganizationType.Association ? 'club' : 'portal';
    }

    isOrgTypeAllowed(type: OrganizationType | string | number | null | undefined, hostname = this.getLocation()?.hostname ?? ''): boolean {
        return this.normalizeOrgType(type) === this.allowedOrgType(hostname);
    }

    buildRedirectUrl(targetSubdomain: AppContext, currentUrl = this.getLocation()?.href ?? ''): string {
        if (!currentUrl) {
            return '';
        }

        const sourceUrl = new URL(currentUrl);
        const targetUrl = new URL(currentUrl);
        const targetHostname = this.buildTargetHostname(sourceUrl.hostname, targetSubdomain);
        const targetPort = this.buildTargetPort(sourceUrl.hostname, sourceUrl.port, targetSubdomain);

        targetUrl.hostname = targetHostname;
        targetUrl.port = targetPort;

        return targetUrl.toString();
    }

    private buildTargetHostname(hostname: string, targetSubdomain: AppContext): string {
        const parts = hostname.split('.');
        const firstLabel = parts[0] ?? '';
        const contextSuffix = this.extractContextSuffix(firstLabel);

        if (contextSuffix !== null) {
            parts[0] = `${targetSubdomain}${contextSuffix}`;
            return parts.join('.');
        }

        if (parts.length <= 2) {
            return `${targetSubdomain}.${hostname}`;
        }

        parts[0] = targetSubdomain;
        return parts.join('.');
    }

    private buildTargetPort(hostname: string, port: string, targetSubdomain: AppContext): string {
        if (!port) {
            return '';
        }

        if (!this.isLocalHost(hostname)) {
            return port;
        }

        if (port === this._localPortMap.portal || port === this._localPortMap.club) {
            return this._localPortMap[targetSubdomain];
        }

        return port;
    }

    private isLocalHost(hostname: string): boolean {
        return hostname === 'localhost'
            || hostname === 'lvh.me'
            || hostname.endsWith('.localhost')
            || hostname.endsWith('.lvh.me');
    }

    private matchesContextLabel(label: string, context: AppContext): boolean {
        return this.extractContextSuffix(label, context) !== null;
    }

    private extractContextSuffix(label: string, expectedContext?: AppContext): string | null {
        const match = label.match(/^(portal|club)(-.+)?$/);

        if (!match) {
            return null;
        }

        const context = match[1] as AppContext;
        if (expectedContext && context !== expectedContext) {
            return null;
        }

        return match[2] ?? '';
    }

    private normalizeOrgType(type: OrganizationType | string | number | null | undefined): OrganizationType | null {
        if (type === null || type === undefined) {
            return null;
        }

        if (typeof type === 'number') {
            return type as OrganizationType;
        }

        if (typeof type === 'string') {
            const trimmedType = type.trim();
            const numericType = Number(trimmedType);

            if (!Number.isNaN(numericType) && trimmedType !== '') {
                return numericType as OrganizationType;
            }

            switch (trimmedType.toLowerCase()) {
                case 'association':
                    return OrganizationType.Association;
                case 'party':
                    return OrganizationType.Party;
                case 'club':
                    return OrganizationType.Club;
                case 'tuna':
                    return OrganizationType.Tuna;
                default:
                    return null;
            }
        }

        return null;
    }

    private getLocation(): Location | null {
        return this._document.defaultView?.location ?? globalThis.location ?? null;
    }
}
